import { NavigateFunction } from "react-router-dom";
import { create } from "zustand";

import { getProfile } from "@/api/me";
import { User, UserDepartment, UserRoleEnum } from "@/api/user/models";
import { getUserInitialDepartmentRoute } from "@/utils/department-routes";
import { zustandHmrFix } from "@/utils/zustand-hmr";

import { userHasAnyRole } from "@/utils";

interface UserStore {
  activeUser: User | null;
  isLoading: boolean;
  initialRoute: string | null;
  shouldPromptAvailability: boolean;
  setInitialRoute: (initialRoute: string | null) => void;
  setShouldPromptAvailability: (shouldPromptAvailability: boolean) => void;
  consumeShouldPromptAvailability: () => boolean;
  setUser: (user: User) => void;
  updateUser: (user: Partial<User>) => void;
  updateProducerAvailability: (department: UserDepartment, isAvailable: boolean) => void;
  setIsLoading: (value: boolean) => void;
  loadUserData: (navigate?: NavigateFunction, abortSignal?: AbortSignal) => Promise<User | null>;
  cleanSession: () => void;
  reset: () => void;
  isAdmin: () => boolean;
}

const initialState = {
  activeUser: null,
  initialRoute: null,
  shouldPromptAvailability: false,
  isLoading: true,
};

export const useUserStore = create<UserStore>((set, get) => ({
  ...initialState,
  isAdmin: () => {
    const user = get().activeUser;

    if (!user) {
      return false;
    }

    // biome-ignore lint/suspicious/noExplicitAny: Dependencia circular impede uso de `UserRoleEnum.ADMINISTRADOR` aqui
    return userHasAnyRole(user, <any>["ADMINISTRADOR"]);
  },
  setInitialRoute: (initialRoute) => {
    set({ initialRoute });
  },
  setShouldPromptAvailability: (shouldPromptAvailability) => {
    set({ shouldPromptAvailability });
  },
  consumeShouldPromptAvailability: () => {
    const shouldPromptAvailability = get().shouldPromptAvailability;
    set({ shouldPromptAvailability: false });

    return shouldPromptAvailability;
  },
  setUser: (activeUser: User) => {
    set({ activeUser });
  },
  updateUser: (info: Partial<User>) => {
    set({ activeUser: <User>{ ...get().activeUser, ...info } });
  },
  updateProducerAvailability: (department, isAvailable) => {
    const activeUser = get().activeUser;
    const producerPositionIndex = activeUser?.positions.findIndex(
      (position) => position.department === department && position.role === UserRoleEnum.PRODUCER,
    );

    if (!activeUser || producerPositionIndex === undefined || producerPositionIndex < 0) {
      return;
    }

    set({
      activeUser: {
        ...activeUser,
        positions: activeUser.positions.map((position, index) =>
          index === producerPositionIndex ? { ...position, isAvailable } : position,
        ),
      },
    });
  },
  setIsLoading: (isLoading: boolean) => {
    set({ isLoading });
  },
  async loadUserData(navigate?: NavigateFunction, abortSignal?: AbortSignal) {
    if (!get().activeUser && !abortSignal?.aborted) {
      set({ isLoading: true });

      try {
        const activeUser = await getProfile(abortSignal);
        if (abortSignal?.aborted) {
          return null;
        }

        const initialRoute = getUserInitialDepartmentRoute(activeUser);

        set({ activeUser, initialRoute });

        if (navigate && initialRoute) {
          navigate(initialRoute);
        }

        return activeUser;
      } catch (error) {
        if (!abortSignal?.aborted) {
          console.error(error);
          get().reset();
        }
      } finally {
        set({ isLoading: false });
      }
    }

    return null;
  },
  cleanSession: () => {
    get().reset();
  },
  reset: () => set({ ...initialState, isLoading: false }),
}));

// Preserva estado durante HMR (Hot Module Reload)
// Solução oficial da comunidade Zustand: https://github.com/pmndrs/zustand/issues/934
if (import.meta.hot) {
  zustandHmrFix("user-store", useUserStore);
}
